import {useRuntimeEnvironment} from '@github-ui/ssr-utils/use-runtime-environment'
import {useTaillightsController, useTaillightsRendering} from '@github-ui/taillights/provider'
import {sourceChunks} from '@github-ui/taillights-worker/source-chunks'
import {clsx} from 'clsx'
import {useCallback, useEffect, useEffectEvent, useLayoutEffect, useMemo, useRef, useState} from 'react'

import {
  gutterFocusScrollTop,
  sameSourceWindow,
  sourceBlockAt,
  type SourceWindow,
  sourceWindowForViewport,
  windowedSourceChunkLines,
} from './source-window'
import {SourceChunk, sourceRenderingStyle} from './SourceChunk'
import styles from './TaillightsCodeView.module.css'
import type {SourceChunksProps} from './WindowedSource'

type Props = Omit<
  SourceChunksProps,
  'linesPerChunk' | 'linesToBuffer' | 'virtualized' | 'plaintext' | 'reverseStickyRefs'
> & {reverseStickyRefs: NonNullable<SourceChunksProps['reverseStickyRefs']>}

function clearInverseStickyLayout(refs: Props['reverseStickyRefs']) {
  refs.offsetRef.current?.style.removeProperty('height')
  const sticky = refs.windowRef.current
  sticky?.style.removeProperty('top')
  sticky?.style.removeProperty('bottom')
  sticky?.removeAttribute('data-sticky-ready')
  refs.sourceRef.current?.style.removeProperty('transform')
}

export function InverseStickySourceChunks({
  view,
  projection,
  renderer,
  safariCSSHighlighting,
  gutter,
  focus,
  scrollMargin,
  scrollPaddingTop = 0,
  preparedFor,
  onRenderError,
  reverseStickyRefs,
}: Props) {
  const controller = useTaillightsController()
  const rendering = useTaillightsRendering()
  const {isBrowser} = useRuntimeEnvironment()
  const profile = preparedFor?.profile ?? controller?.profile
  const lineHeight = profile?.lineHeight ?? rendering?.lineHeight ?? 20
  const fallbackStyle = useMemo(() => sourceRenderingStyle(profile, rendering), [profile, rendering])
  const blocks = useMemo(
    () => view?.blocks ?? sourceChunks(projection.lineNumbers, windowedSourceChunkLines),
    [view, projection.lineNumbers],
  )
  const chunksRef = useRef<HTMLDivElement>(null)
  const sampledWindowRef = useRef<SourceWindow | undefined>(undefined)
  const {focusedLine, request: focusRequest, onFocusChange} = focus
  const handledFocusRef = useRef(focusRequest)
  const [attachmentRevision, setAttachmentRevision] = useState(0)
  const [range, setRange] = useState<SourceWindow>(() => ({
    first: 0,
    last: Math.min(2, blocks.length) - 1,
    viewportHeight: 0,
  }))
  const ownerLine = gutter.actions.owner?.lineNumber
  const first = Math.min(range.first, blocks.length - 1)
  const last = Math.min(range.last, blocks.length - 1)
  const mounted = Array.from({length: Math.max(0, last - first + 1)}, (_, index) => first + index)
  for (const line of [focusedLine, ownerLine]) {
    if (line == null) continue
    const display = projection.sourceToDisplay.get(line - 1)
    if (display === undefined) continue
    const index = sourceBlockAt(blocks, display)
    if (!mounted.includes(index)) mounted.push(index)
  }
  mounted.sort((a, b) => a - b)
  const start = (blocks[first]?.displayStart ?? 0) * lineHeight
  const endBlock = blocks[last]
  const end = endBlock ? (endBlock.displayStart + Math.max(1, endBlock.lineNumbers.length)) * lineHeight : lineHeight
  const height = Math.max(lineHeight, end - start)
  // Keep React's mount height constant; the layout effect owns live height and transform.
  const [initialHeight] = useState(height)
  const attachChunks = useCallback(
    (node: HTMLDivElement) => {
      chunksRef.current = node
      // Reattachment can reuse the same node and range after Activity/Suspense hides it.
      setAttachmentRevision(previous => previous + 1)
      return () => {
        // Ref cleanup precedes the next layout effects, including synchronous print materialization.
        clearInverseStickyLayout(reverseStickyRefs)
        chunksRef.current = null
      }
    },
    [reverseStickyRefs],
  )

  const sampleViewport = useEffectEvent(() => {
    const next = sourceWindowForViewport(blocks, lineHeight, window.scrollY - scrollMargin, window.innerHeight)
    if (sampledWindowRef.current && sameSourceWindow(sampledWindowRef.current, next)) return
    sampledWindowRef.current = next
    setRange(previous => (sameSourceWindow(previous, next) ? previous : next))
  })

  useEffect(() => {
    // eslint-disable-next-line github/prefer-observers -- Distant jumps need native page offsets, not delayed intersection delivery.
    window.addEventListener('scroll', sampleViewport)
    // eslint-disable-next-line github/prefer-observers -- Window extent determines the inverse-sticky insets.
    window.addEventListener('resize', sampleViewport)
    sampleViewport()
    return () => {
      window.removeEventListener('scroll', sampleViewport)
      window.removeEventListener('resize', sampleViewport)
    }
  }, [])

  useLayoutEffect(() => {
    if (!isBrowser) return
    if (range.viewportHeight === 0) {
      sampleViewport()
      return
    }
    const offset = reverseStickyRefs.offsetRef.current
    const sticky = reverseStickyRefs.windowRef.current
    const source = reverseStickyRefs.sourceRef.current
    const chunks = chunksRef.current
    // Ancestor refs attach later in this commit; the attachment update retries before paint.
    if (!offset || !sticky || !source || !chunks) return
    const viewportHeight = window.innerHeight
    const actualScrollY = window.scrollY
    const viewport = sticky.firstElementChild
    const innerScrollTop = viewport instanceof HTMLElement ? viewport.scrollTop : 0
    let targetScrollY = actualScrollY
    const reveal = focusRequest && focusRequest !== handledFocusRef.current
    if (reveal) {
      const display = projection.sourceToDisplay.get(focusRequest.line - 1)
      if (display !== undefined) {
        const rowTop = scrollMargin + display * lineHeight
        targetScrollY = gutterFocusScrollTop(rowTop, lineHeight, scrollPaddingTop, actualScrollY, viewportHeight)
      }
    }
    const next = sourceWindowForViewport(blocks, lineHeight, targetScrollY - scrollMargin, viewportHeight)
    // Reconciliation can change the window without a scroll event.
    sampledWindowRef.current = next
    // Correct a stale scroll/fold snapshot before paint, without positioning it as the new slice.
    if (!sameSourceWindow(range, next)) {
      setRange(next)
      return
    }
    const inset = `${Math.min(0, viewportHeight - height)}px`
    const translate = `translateY(${-start}px)`
    const write = (node: HTMLElement, key: string, value: string) => {
      if (node.style.getPropertyValue(key) !== value) node.style.setProperty(key, value)
    }
    // Update live geometry together, only after the committed slice matches.
    write(offset, 'height', `${start}px`)
    write(sticky, 'top', inset)
    write(sticky, 'bottom', inset)
    write(source, 'transform', translate)
    write(chunks, 'height', `${height}px`)
    write(chunks, 'transform', translate)
    if (!sticky.hasAttribute('data-sticky-ready')) sticky.setAttribute('data-sticky-ready', 'true')
    if (innerScrollTop && viewport instanceof HTMLElement) viewport.scrollTo({top: 0, behavior: 'instant'})
    if (targetScrollY !== actualScrollY) window.scrollTo({top: targetScrollY, behavior: 'instant'})
    if (reveal) handledFocusRef.current = focusRequest
  }, [
    isBrowser,
    attachmentRevision,
    reverseStickyRefs,
    blocks,
    lineHeight,
    scrollMargin,
    scrollPaddingTop,
    range,
    start,
    height,
    projection,
    focusRequest,
    handledFocusRef,
  ])

  const preferredLine = focusedLine ?? ownerLine ?? (gutter.anchorRange ? gutter.anchorRange.start.line + 1 : null)
  const tabStopLine =
    preferredLine !== null && mounted.some(index => blocks[index]?.lineNumbers.includes(preferredLine - 1))
      ? preferredLine
      : (blocks[first]?.lineNumbers[0] ?? -1) + 1
  return (
    <div
      ref={attachChunks}
      className={clsx(styles.WindowChunks, styles.InverseStickyChunks)}
      style={{height: initialHeight}}
    >
      {mounted.map(index => {
        const block = blocks[index]
        if (!block) return null
        return (
          <div
            key={block.anchor}
            className={styles.PositionedChunk}
            style={{
              height: Math.max(1, block.lineNumbers.length) * lineHeight,
              transform: `translateY(${block.displayStart * lineHeight}px)`,
            }}
          >
            <SourceChunk
              virtualIndex={index}
              directDomUpdates={false}
              windowLocal
              layout={block}
              block={view?.blocks[index]}
              renderer={renderer}
              safariCSSHighlighting={safariCSSHighlighting}
              gutter={gutter}
              lineHeight={lineHeight}
              tabStopLine={block.lineNumbers.includes(tabStopLine - 1) ? tabStopLine : null}
              onFocusChange={onFocusChange}
              onRenderError={onRenderError}
              paintReady={controller !== null && preparedFor === controller}
              fallbackStyle={fallbackStyle}
              showGutter
            />
          </div>
        )
      })}
    </div>
  )
}
